/**
 * Community.tsx — the /community landing (Community Hub), on the house design system.
 *
 * THE PAGE IS THE FRAME LEARNING USES; THE ONE DIALOG IS THE FRAME UPLOAD USES.
 * This is the Learning Hub migration applied to the forum landing: the page wears `.cm-*`
 * (communityPage.css), which is `.lh-*`'s geometry — a full-bleed 193px navy band flush under
 * the header, then sections on one 64px inset, each a 32px head row — and the single dialog is the
 * upload flow's real `OverlayFrame` + `ModalShell`, trapped and restored by `useLearningModal`,
 * exactly as the three Learning dialogs are. What was the `COMMUNITY_STYLES` `--tb-*` block and a
 * `max-width: 60rem` clamp (which stranded the right half of a wide screen) is now design-system
 * tokens and a full-bleed layout.
 *
 * SCOPE OF THE MIGRATION. Only this landing moves in this pass. Channel.tsx and ThreadView.tsx
 * still import COMMUNITY_STYLES / ProvisionalBanner from ./shared, so those exports STAY for them;
 * this file simply stops importing them. `Community` and `communityLoader` keep their names, so
 * router.tsx is untouched.
 *
 * WHAT MOVED INTO A DIALOG. Only "Ask for help": a self-contained composer plus the raiser's own
 * request list, read occasionally, not the point of the page. Everything else — the channels, the
 * saved threads, the review-queue link — stays inline. Nothing was moved to hide it: the dialog is
 * reached from one labelled button in the band, and the button says what it opens.
 *
 * SECURITY (unchanged). The channel list comes from the backend, which returns ONLY channels the
 * caller may read — a non-Champion never sees "Champions Only" here, and hitting its endpoints
 * directly 404s (CH-F15/NFR03). We never derive the list from the shared CHANNELS constant. The
 * raiser of a support request is the session identity server-side, so nothing here sends a name or
 * id, and there is no way to raise a request as somebody else. Every backend call is byte-for-byte
 * what it was before the reskin.
 */
import { useCallback, useRef, useState, type RefObject } from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router';
import { AnimatePresence } from 'motion/react';
import { apiGet, type ChannelSummary, type ThreadSummary } from './api';
import {
  getMySupportRequests,
  postAskForHelp,
  SUPPORT_BODY_MAX_LENGTH,
  SUPPORT_BODY_MIN_LENGTH,
  type MySupportRequest,
} from './supportApi';
import { SignedOut, shortWhen } from './shared';
import { Button } from '../tools/upload/design/Button';
import { ModalShell } from '../tools/upload/design/ModalShell';
import { OverlayFrame } from '../tools/upload/design/OverlayFrame';
import { useLearningModal } from '../learning/useLearningModal';
// NOTE: communityPage.css is imported at the app root (src/main.tsx), NOT here. A component-level
// import lands in the route's JS chunk and applies one frame after the markup paints, flashing the
// full-bleed navy band in default flow on a cold load — the same reason frame.css lives in main.tsx.

/**
 * `myRequests` is `'unavailable'` when that one read failed — see the loader. A failure there must
 * not empty the list, because the composer tells the raiser their request appears in it.
 */
export type CommunityLoaderData =
  | {
      state: 'ok';
      channels: ChannelSummary[];
      saved: ThreadSummary[];
      myRequests: MySupportRequest[] | 'unavailable';
    }
  | { state: 'signedOut' };

/**
 * GET /api/community/channels → the readable channels, GET /api/community/saved → the caller's saved
 * threads, and GET /api/me/support-requests → the caller's own support requests (CH-F13's raiser
 * half). A 401 on any of them is the signed-out case (show the Sign-in prompt). The community family
 * is always mounted, so there is no feature-disabled arm; any other non-OK is a real fault.
 *
 * THE SUPPORT READ IS THE ONE EXCEPTION to that last sentence. The channels ARE this page; a fault
 * reading them is worth the error boundary. Someone's own request list is a panel on it, so a fault
 * there degrades to a stated 'unavailable' instead of replacing the whole hub with an error — but it
 * is NOT silently emptied, because an empty list beside "your request is listed below" would tell the
 * person their request had not been raised when it had.
 */
export async function communityLoader(): Promise<CommunityLoaderData> {
  const channelsRes = await apiGet<{ channels: ChannelSummary[] }>('/api/community/channels');
  if (channelsRes.kind === 'unauthorized') return { state: 'signedOut' };
  if (channelsRes.kind === 'error') {
    throw new Error(`GET /api/community/channels failed (${channelsRes.status})`);
  }

  const savedRes = await apiGet<{ threads: ThreadSummary[] }>('/api/community/saved');
  if (savedRes.kind === 'unauthorized') return { state: 'signedOut' };
  if (savedRes.kind === 'error') {
    throw new Error(`GET /api/community/saved failed (${savedRes.status})`);
  }

  const mineRes = await getMySupportRequests();
  if (mineRes.kind === 'unauthorized') return { state: 'signedOut' };

  return {
    state: 'ok',
    channels: channelsRes.body.channels,
    saved: savedRes.body.threads,
    myRequests: mineRes.kind === 'ok' ? mineRes.body.requests : 'unavailable',
  };
}

/**
 * A one-line "what this channel is for" per channel, keyed by the shared slug (vocabulary.ts's
 * CHANNELS). The backend's ChannelSummary carries no description — it returns id/slug/name/canPost
 * only — so this is presentation copy that lives with the page, NOT an authority on anything: the
 * server still decides which channels the caller sees, and a slug we cannot name falls back to a
 * generic line rather than asserting a purpose we do not know. Kept deliberately short (one clause)
 * so it reads as a subtitle under the card title, not a paragraph.
 */
const CHANNEL_BLURBS: Readonly<Record<string, string>> = {
  questions: 'Ask a question and get help from colleagues across the business.',
  'promoted-tools': 'Discover tools worth trying, highlighted for the whole community.',
  'swat-suggestions': 'Suggest improvements and raise ideas for the SWAT team.',
  'champion-announcements': 'Read the latest updates and announcements from Champions.',
  'champions-only': 'A private space for Champions to share notes and coordinate.',
  'swat-only': 'A private space for the SWAT team to plan and compare notes.',
};

/** The blurb for a channel, falling back to a generic line for any slug we do not recognise. */
function channelBlurb(slug: string): string {
  return CHANNEL_BLURBS[slug] ?? 'Read and join the conversation with your colleagues.';
}

/**
 * The modal title, matching the upload flow's own and the Learning dialogs' (24px/300 on `--text`,
 * `min-w-0` so a long title truncates rather than shoving the header's right edge). Copied from
 * LearningHub's `MODAL_TITLE` so the forum's one dialog wears the same header as every other.
 */
const MODAL_TITLE = 'min-w-0 text-xl font-light text-text';

/** The dialog body's vertical rhythm — verbatim LearningHub `DIALOG_BODY`. */
const DIALOG_BODY = 'grid gap-8 pt-11 pb-11';

// ── page ─────────────────────────────────────────────────────────────────────────────────────

export function Community() {
  const data = useLoaderData() as CommunityLoaderData;
  if (data.state === 'signedOut') return <SignedOut title="Community" returnTo="/community" />;

  return <CommunityBody data={data} />;
}

function CommunityBody({ data }: { data: Extract<CommunityLoaderData, { state: 'ok' }> }) {
  const { channels, saved, myRequests } = data;

  // Is the Ask-for-help dialog open? Null = the page itself. The button that opens it keeps a ref so
  // focus returns there on close (WCAG 2.4.3), the house pattern the Learning dialogs use.
  const [helpOpen, setHelpOpen] = useState(false);
  const helpTrigger = useRef<HTMLButtonElement>(null);
  const closeHelp = useCallback(() => setHelpOpen(false), []);

  return (
    <section aria-labelledby="community-heading" className="tb-community cm-page">
      {/* ── Band: the page's name and what it is for (left), the one action that opens a dialog
             (right). Same silhouette as Learning and Profile. ── */}
      <div className="cm-band-bleed">
        <div className="cm-band">
          <div className="cm-band__main">
            <h1 id="community-heading">Community</h1>
            <p className="cm-band__lede">
              Ask a question, share what you have learned, and help your colleagues — so good
              answers stay on the platform instead of disappearing into private messages.
            </p>
          </div>

          {/* The one overlay on this page. A surface card on the navy holding a single button; the
              composer and the raiser's request list live inside the dialog it opens. */}
          <div className="cm-help-card">
            <p className="cm-help-card__title">Need something the forum can’t answer?</p>
            <p className="cm-help-card__hint">
              An account, an appointment, or a platform problem — raise it with the support team.
              It’s private, not a forum post.
            </p>
            <Button
              ref={helpTrigger}
              variant="solid-square"
              size="sm"
              className="cm-btn-navy cm-help-card__cta"
              aria-haspopup="dialog"
              onClick={() => setHelpOpen(true)}
            >
              Ask for help
            </Button>
          </div>
        </div>
      </div>

      {/* ── Channels: whole-card links that fill the width (CH-F01/F15). ── */}
      <section aria-labelledby="channels-heading" className="cm-section">
        <div className="cm-section-head">
          <h2 id="channels-heading">Channels</h2>
        </div>
        {channels.length === 0 ? (
          <p className="cm-empty">No channels are available to you.</p>
        ) : (
          <ul className="cm-grid">
            {channels.map((c) => (
              <li key={c.id}>
                <Link className="cm-card" to={`/community/${c.slug}`}>
                  <span className="cm-card__title">{c.name}</span>
                  <span className="cm-card__desc">{channelBlurb(c.slug)}</span>
                  <span className="cm-card__meta">
                    {c.canPost ? 'You can post here.' : 'You can read this channel.'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Saved threads: the caller's private read-later list (CH-F12). ── */}
      <section aria-labelledby="saved-heading" className="cm-section">
        <div className="cm-section-head">
          <h2 id="saved-heading">Saved threads</h2>
        </div>
        <p className="cm-note">Threads you saved to read later. Only you can see this list.</p>
        {saved.length === 0 ? (
          <p className="cm-empty">You have not saved any threads yet.</p>
        ) : (
          <ul className="cm-grid">
            {saved.map((t) => (
              <li key={t.threadId}>
                <Link className="cm-card" to={`/community/thread/${t.threadId}`}>
                  <span className="cm-card__title">{t.title}</span>
                  <span className="cm-card__meta">
                    {t.replyCount} {t.replyCount === 1 ? 'reply' : 'replies'} · last activity{' '}
                    {shortWhen(t.lastPostAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/*
        A quiet link to the moderator review queue (CH-F17) — the same /moderation surface the rest
        of the admin/moderator queues use (ChampionRole/SWATMember/PlatformOwner, both forum posts
        AND tool comments). Visible to everyone: the backend 403s for a non-moderator and the page
        renders NotPermitted in-page, matching /review and /support-queue's own posture. We show no
        role-dependent label here; the client never learns who is a moderator.
      */}
      <p className="cm-footnote">
        <Link className="cm-prose-link" to="/moderation">
          Review queue
        </Link>{' '}
        — for the colleagues who help keep the forum tidy.
      </p>

      <AnimatePresence>
        {helpOpen && (
          <AskForHelpDialog
            key="ask-for-help"
            requests={myRequests}
            onClose={closeHelp}
            returnFocusRef={helpTrigger}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

// ── the Ask-for-help dialog ────────────────────────────────────────────────────────────────────

/**
 * "Ask for help" (CH-F13 / FB-F15) — the raiser's entry point, now an overlay dialog rather than an
 * inline form. The composer and the raiser's own request list share the one modal: you raise a
 * request and immediately see it land in the list beneath, without leaving the dialog.
 *
 * WHAT IT SENDS is `{origin: 'ask_for_help', body}` and nothing else (see supportApi.ts). The raiser
 * is the session identity server-side, so there is no name or id to send. Local state only.
 *
 * THE BOUNDS ARE MIRRORED, NOT OWNED. The button stays disabled below the server's own minimum so the
 * usual case is not a round trip to a 400, but the 400 is still handled — the server measures after
 * trim and it is the authority.
 *
 * WHAT THE COPY MAY PROMISE is only what exists today: the request reaches the triage queue, and the
 * raiser can see it and its status history in the list below. It does NOT say the person will be
 * notified — FB-F16's kind-7 notification is written server-side, but nothing in the web renders it
 * yet (NT-F09 open), so promising a message here would promise a screen the platform does not have.
 */
function AskForHelpDialog({
  requests,
  onClose,
  returnFocusRef,
}: {
  requests: MySupportRequest[] | 'unavailable';
  onClose: () => void;
  returnFocusRef: RefObject<HTMLButtonElement>;
}) {
  const { dialogRef, onKeyDown } = useLearningModal(onClose, returnFocusRef);
  const revalidator = useRevalidator();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raised, setRaised] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setRaised(null);
    setBusy(true);
    const res = await postAskForHelp(body);
    setBusy(false);
    if (res.kind === 'ok') {
      // The status comes back from the row the database wrote, so what we show the raiser is what
      // their request actually is — never a word we chose here.
      setRaised(res.body.status);
      setBody('');
      revalidator.revalidate(); // re-read /api/me/support-requests so the new row appears below
      return;
    }
    if (res.kind === 'unauthorized') {
      setError('Your session has expired. Please sign in again.');
      return;
    }
    if (res.kind === 'error' && res.code === 'invalid_request') {
      setError(
        `Please describe what you need help with — at least ${String(SUPPORT_BODY_MIN_LENGTH)} characters.`,
      );
      return;
    }
    setError('Something went wrong raising your request. Please try again.');
  }

  const trimmed = body.trim();
  const canSubmit =
    trimmed.length >= SUPPORT_BODY_MIN_LENGTH && trimmed.length <= SUPPORT_BODY_MAX_LENGTH && !busy;

  return (
    <OverlayFrame dialogRef={dialogRef} onKeyDown={onKeyDown} onClose={onClose}>
      <AnimatePresence mode="wait" initial={false}>
        <ModalShell
          key="ask-for-help"
          labelledBy="help-title"
          headerInset="title"
          header={
            <h1 id="help-title" className={MODAL_TITLE}>
              Ask for help
            </h1>
          }
          footer={
            <div className="border-t border-ink">
              <div className="flex flex-wrap items-center justify-between gap-7 px-modal-x py-11 pr-modal-footer-r">
                <div className="flex items-center gap-modal-footer" />
                <div className="flex items-center gap-modal-footer">
                  <Button variant="outline-square" size="xs" onClick={onClose}>
                    Close
                  </Button>
                  <Button
                    variant="outline-square"
                    size="xs"
                    state={canSubmit ? 'default' : 'disabled'}
                    onClick={() => void submit()}
                  >
                    {busy ? 'Sending…' : 'Ask for help'}
                  </Button>
                </div>
              </div>
            </div>
          }
        >
          <div className={DIALOG_BODY}>
            <p className="max-w-[70ch] text-xs font-light text-text">
              For something the forum cannot answer — an account, an appointment, or a platform
              problem. This is not a forum post: no colleague browsing the Community sees it. It
              goes to the SWAT team and the platform owner, who work through the support queue.
            </p>

            {error && (
              <p role="alert" className="text-xs font-light text-error">
                {error}
              </p>
            )}
            {raised && (
              <p role="status" className="text-xs font-light text-text">
                Raised — your request is with the support team, currently <strong>{raised}</strong>.
                It is in your list below, with any reason a triager records.
              </p>
            )}

            {/* The composer. A hand-rolled field rather than FormField because we keep `maxLength`
                and the live character count, and the dialog body owns its own type (text-xs). */}
            <div className="grid gap-field-gap">
              <label htmlFor="help-body" className="text-sm font-medium text-ink">
                What do you need help with?
              </label>
              <textarea
                id="help-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={SUPPORT_BODY_MAX_LENGTH}
                rows={5}
                placeholder="Describe what you were trying to do and what happened."
                className="w-full min-w-0 resize-y rounded-md border border-border-subtle bg-transparent px-field-x py-field-x text-xs font-light text-ink outline-none focus-visible:border-ring-focus"
              />
              <p className="text-xs font-light text-text">
                {trimmed.length < SUPPORT_BODY_MIN_LENGTH
                  ? `At least ${String(SUPPORT_BODY_MIN_LENGTH)} characters.`
                  : `${String(body.length)} / ${String(SUPPORT_BODY_MAX_LENGTH)}`}
              </p>
            </div>

            {/* The raiser's own requests, newest-first as the server returns them. */}
            <div className="grid gap-3">
              <h2 className="text-sm font-medium text-ink">Your support requests</h2>
              <p className="text-xs font-light text-text">
                Only you and the colleagues who triage them can see these.
              </p>
              {requests === 'unavailable' ? (
                <p className="text-xs font-light text-text">
                  We could not load your requests just now. Refresh the page to try again — anything
                  you have raised is safe.
                </p>
              ) : requests.length === 0 ? (
                <p className="text-xs font-light text-text">
                  You have not raised a support request.
                </p>
              ) : (
                <ul className="cm-requests">
                  {requests.map((r) => (
                    <li className="cm-request" key={r.requestId}>
                      {/* The tool a `tool_report` request names, when there is one. An ask-for-help
                          request carries no tool and gets no title line — its own words lead. */}
                      {r.toolName !== null && (
                        <p className="text-sm font-medium text-ink" style={{ margin: 0 }}>
                          {r.toolName}
                        </p>
                      )}
                      <p className="cm-request__body">{r.body}</p>
                      <p className="cm-request__meta">
                        Status: <strong>{r.status}</strong> · raised {shortWhen(r.createdAt)}
                      </p>
                      {/* Oldest-first, each with the reason the triager had to give (NT-F17) — and
                          never who they were: the raiser is owed the outcome and the reason. */}
                      {r.history.map((h) => (
                        <p className="cm-request__meta" key={`${h.toStatus}-${h.changedAt}`}>
                          → {h.toStatus} ({shortWhen(h.changedAt)}): {h.reason}
                        </p>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </ModalShell>
      </AnimatePresence>
    </OverlayFrame>
  );
}
